import type { NumericValue, RatingValue } from './types'

import { MoodysRating, SPRating } from '@moment/trading-types/reference-data'

import { ValueUnit } from '@/types/enums/value-unit'
import { formatNumber, formatPercentage } from '@/utils/presentation/formatting'

export const MIN_INDEX = 0
export const CURRENT_INDEX = 1
export const PROPOSED_INDEX = 2
export const TARGET_INDEX = 3
export const MAX_INDEX = 4

const MIN_SPACING_PERCENT_BETWEEN_VALUES = 18

export function isRatingValue(
  value: RatingValue | NumericValue,
): value is RatingValue {
  return 'rating' in value
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

// Get the unit from a value or default unit
export function getValueUnit(
  value: RatingValue | NumericValue,
  defaultUnit: ValueUnit = ValueUnit.Percentage,
): ValueUnit {
  if (isRatingValue(value)) {
    return ValueUnit.Rating
  }

  return value.unit ?? defaultUnit
}

// Helper to extract numeric value from either rating or numeric value
export function extractValue(
  value: RatingValue | NumericValue | null | undefined,
): number | null {
  if (!value) return null

  if (isRatingValue(value)) {
    const rating = value.rating

    // Try SP Rating first
    const spRatingValues = Object.values(SPRating)
    const spIndex = spRatingValues.indexOf(rating as SPRating)

    if (spIndex !== -1) {
      // Found in SP ratings, return inverted index
      return spRatingValues.length - spIndex
    }

    // Try Moody's Rating
    const moodysRatingValues = Object.values(MoodysRating)
    const moodysIndex = moodysRatingValues.indexOf(rating as MoodysRating)

    if (moodysIndex !== -1) {
      // Found in Moody's ratings, return inverted index
      return moodysRatingValues.length - moodysIndex
    }

    return null
  }

  return value.value
}

// Extract and sort values for processing
export function extractAndSortValues(extractedValues: {
  current: number | null
  proposed: number | null
  target: number | null
  min: number | null
  max: number | null
}) {
  // Create and filter in a single pass
  const allValues = [
    { type: 'current', value: extractedValues.current },
    { type: 'proposed', value: extractedValues.proposed },
    { type: 'target', value: extractedValues.target },
    { type: 'min', value: extractedValues.min },
    { type: 'max', value: extractedValues.max },
  ].filter((item) => item.value !== null)

  // Sort values from smallest to largest
  return [...allValues].sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
}

// Calculate min/max boundaries from the values
export function calculateBoundaries(
  sortedValues: Array<{ value: number | null }>,
) {
  if (sortedValues.length === 0) return { min: 0, max: 100 }
  return {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: sortedValues[0]!.value ?? 0,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: sortedValues[sortedValues.length - 1]!.value ?? 100,
  }
}

// Calculate positions as percentages with modified overlap behavior
export function calculateRelativePosition(
  values: Array<number | null>,
  boundaries: { min: number; max: number },
) {
  // Filter out null values
  const nonNullValues = values.filter((v) => v !== null) as number[]

  // If no values or only one value, return simple positioning
  if (nonNullValues.length <= 1) {
    return values.map((v) => (v === null ? null : 50))
  }

  // Check if all values are the same
  const allSameValue = nonNullValues.every((v) => v === nonNullValues[0])

  // Special case: If all values are identical, place min left, max right, others center
  if (allSameValue && nonNullValues.length > 1) {
    // Create a result array with nulls in the same positions
    const result: Array<number | null> = values.map((v) =>
      v === null ? null : 50,
    )

    // Place min at 0% if it exists
    if (values[MIN_INDEX] !== null) {
      result[MIN_INDEX] = 0
    }

    // Place max at 100% if it exists
    if (values[MAX_INDEX] !== null) {
      result[MAX_INDEX] = 100
    }

    return result
  }

  // First, calculate raw positions based on the range
  const range = boundaries.max - boundaries.min
  // Ensure we have a non-zero range to avoid division by zero
  const effectiveRange = range === 0 ? 1 : range

  // Calculate raw positions first
  const rawPositions = values.map((v, index) => {
    if (v === null) return { index, value: null, rawPosition: null }

    const percentage = (v - boundaries.min) / effectiveRange
    return {
      index,
      value: v,
      rawPosition: percentage * 100,
    }
  })

  // Create a result array
  const result: Array<number | null> = Array(values.length).fill(null)

  // Combine all non-null positions, sort by position
  const allPositions = rawPositions
    .filter((item) => item.value !== null && item.rawPosition !== null)
    .sort((a, b) => (a.rawPosition ?? 0) - (b.rawPosition ?? 0))

  // First pass: identify values that are exactly the same and assign them the same position
  // Group values by exact value
  const valueGroups = new Map<number, { indices: number[]; position: number }>()

  allPositions.forEach((item) => {
    const value = item.value as number
    if (!valueGroups.has(value)) {
      valueGroups.set(value, {
        indices: [item.index],
        position: item.rawPosition as number,
      })
    } else {
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      valueGroups.get(value)!.indices.push(item.index)
    }
  })

  // Find min required spacing between each distinct value
  const minSpacing = MIN_SPACING_PERCENT_BETWEEN_VALUES

  // Second pass: Check if we need to adjust for minimum spacing,
  // and if so, use a proportional scaling approach
  const distinctValues = Array.from(valueGroups.values()).sort(
    (a, b) => a.position - b.position,
  )

  // Find minimum spacing between consecutive distinct values
  let needsScaling = false
  let minActualSpacing = 100 // Start with maximum possible

  for (let i = 1; i < distinctValues.length; i++) {
    const spacing =
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      distinctValues[i]!.position - distinctValues[i - 1]!.position
    minActualSpacing = Math.min(minActualSpacing, spacing)
    if (spacing < minSpacing) {
      needsScaling = true
    }
  }

  // If we need to scale to accommodate minimum spacing
  if (needsScaling && distinctValues.length > 1) {
    // Calculate scale factor needed
    const totalDistinctPoints = distinctValues.length
    const currentSpan =
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      distinctValues[totalDistinctPoints - 1]!.position -
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      distinctValues[0]!.position
    const requiredSpan = (totalDistinctPoints - 1) * minSpacing

    // If we can't fit all with minimum spacing, scale proportionally
    // while preserving the relative positions as much as possible
    if (currentSpan < requiredSpan) {
      // Scale factor to ensure minimum spacing
      const scaleFactor = requiredSpan / currentSpan

      // Create buffer zone on both sides
      const buffer = (100 - requiredSpan) / 2

      // Apply scale and shift
      for (let i = 0; i < distinctValues.length; i++) {
        // Scale the relative position using the scaleFactor directly
        const relativePos =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          distinctValues[i]!.position - distinctValues[0]!.position
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        distinctValues[i]!.position = buffer + relativePos * scaleFactor
      }
    } else {
      // If we have enough space but some points are too close,
      // push apart only those that are too close
      for (let i = 1; i < distinctValues.length; i++) {
        const spacing =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          distinctValues[i]!.position - distinctValues[i - 1]!.position
        if (spacing < minSpacing) {
          // Calculate the push needed
          const push = minSpacing - spacing

          // Push the current and all following points to the right
          for (let j = i; j < distinctValues.length; j++) {
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            distinctValues[j]!.position += push
          }
        }
      }

      // If this pushed anything beyond 100%, scale everything back
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      if (distinctValues[distinctValues.length - 1]!.position > 100) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        const scale = 100 / distinctValues[distinctValues.length - 1]!.position
        for (let i = 0; i < distinctValues.length; i++) {
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          distinctValues[i]!.position *= scale
        }
      }
    }
  }

  // Apply the final positions to the result array
  for (const group of distinctValues) {
    for (const index of group.indices) {
      result[index] = group.position
    }
  }

  return result
}

// Format a numeric value based on its unit type
function formatValueByUnit(value: number, unit: ValueUnit): string {
  switch (unit) {
    // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: Bulk suppression as part of biome migration
    default:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case ValueUnit.Percentage:
      return formatPercentage(value, {
        numDigits: 3,
        removeTrailingZeros: true,
      })
    case ValueUnit.Years:
      return `${formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} yr${value === 1 ? '' : 's'}`
    case ValueUnit.Dollars:
      return `$${formatNumber(value)}`
    case ValueUnit.Rating:
      return String(value)
  }
}

// Get display value for rating or numeric value
export function getDisplayValue(
  value: RatingValue | NumericValue,
  defaultUnit: ValueUnit = ValueUnit.Percentage,
): string {
  if (isRatingValue(value)) {
    return value.rating
  }

  // Use the specified unit from the value or fall back to default
  const unit = value.unit ?? defaultUnit

  // Use the provided label if available, otherwise format the value
  if (value.label && value.label.trim() !== '') {
    return value.label
  }

  return formatValueByUnit(value.value, unit)
}

// Check if values are out of bounds
export function checkValueBounds(
  extractedValues: {
    current: number | null
    min: number | null
    max: number | null
  },
  allSameValue: boolean,
) {
  // When all values are identical, nothing is out of bounds
  const isCurrentBelowMin =
    !allSameValue &&
    extractedValues.current !== null &&
    extractedValues.min !== null &&
    extractedValues.current < extractedValues.min

  const isCurrentAboveMax =
    !allSameValue &&
    extractedValues.current !== null &&
    extractedValues.max !== null &&
    extractedValues.current > extractedValues.max

  return {
    isCurrentBelowMin,
    isCurrentAboveMax,
    isCurrentOutOfBounds: isCurrentBelowMin || isCurrentAboveMax,
  }
}

// Determine which position to connect with dotted line
export function determineConnectionPosition(
  current: number | null,
  isCurrentBelowMin: boolean,
  isCurrentAboveMax: boolean,
  positions: {
    min: number | null
    max: number | null
    target: number | null
  },
) {
  if (current === null) return null
  if (isCurrentBelowMin) return positions.min
  if (isCurrentAboveMax) return positions.max
  return positions.target
}
